import React from "react";
import { AlertTriangle, Check, X, ShieldCheck, PoundSterling } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const CAT_CLS = {
  Risk: "text-red-700 bg-red-50 border-red-200",
  "Stage-gate": "text-amber-700 bg-amber-50 border-amber-200",
  Benefit: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Decision: "text-indigo-700 bg-indigo-50 border-indigo-200",
  Confidence: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
};
const sevDot = (s) => s === "high" ? "bg-red-500" : s === "medium" ? "bg-amber-500" : "bg-zinc-400";
const Stat = ({ label, value, cls = "text-black" }) => (
  <div className="text-center"><div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{label}</div></div>
);

export default function DeliveryAlerts({ clientId, data, reload }) {
  const [busy, setBusy] = React.useState(false);
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await reload(); } catch { toast.error("Action failed"); } finally { setBusy(false); }
  };
  const ack = (a) => act(() => api.post(`/admin/enterprise360/clients/${clientId}/alerts/${a.id}/ack`));
  const dismiss = (a) => act(() => api.post(`/admin/enterprise360/clients/${clientId}/alerts/${a.id}/dismiss`));

  const c = data?.counts || {};
  const items = data?.items || [];
  return (
    <div data-testid="delivery-alerts">
      <p className="text-[12px] text-zinc-500 max-w-2xl mb-3">
        <AlertTriangle size={12} className="inline mr-1" /> Foresight, not reporting — deterministic rules watch the live registers
        and raise an alert the moment a risk turns red, a gate slips, a benefit falls behind, a decision goes overdue, or confidence drops a band. Alerts auto-resolve when the condition clears.
      </p>
      <div data-testid="alerts-summary" className="flex items-center gap-6 flex-wrap border border-zinc-200 rounded-lg px-4 py-3 mb-4">
        <Stat label="Active" value={c.active ?? 0} cls={c.active ? "text-black" : "text-emerald-600"} />
        <Stat label="New" value={c.new ?? 0} cls={c.new ? "text-red-600" : "text-black"} />
        <Stat label="High severity" value={c.high ?? 0} cls={c.high ? "text-red-600" : "text-black"} />
        <Stat label="Acknowledged" value={c.acknowledged ?? 0} />
      </div>
      {items.length === 0 ? (
        <div data-testid="alerts-empty" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-6 text-center">
          <ShieldCheck size={18} className="inline mr-1" /> All clear — no active early-warning alerts.
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg divide-y" data-testid="alerts-list">
          {items.map((a) => (
            <div key={a.id} data-testid={`alert-row-${a.id}`} className="px-3 py-3 flex items-start gap-3 flex-wrap">
              <span data-testid={`alert-severity-${a.id}`} className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-none ${sevDot(a.severity)}`} title={a.severity} />
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${CAT_CLS[a.category] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{a.category}</span>
                  <span className="text-[13px] text-zinc-800 font-medium">{a.title}</span>
                  {a.value_label && <span className="inline-flex items-center gap-0.5 text-[11px] text-[var(--e360-primary)] font-semibold"><PoundSterling size={11} />{a.value_label} exposed</span>}
                  {a.status === "acknowledged" && <span className="text-[10px] text-zinc-500 bg-zinc-100 rounded-full px-2 py-0.5">Acknowledged</span>}
                </div>
                <p className="text-[12px] text-zinc-500 mt-0.5">{a.detail}</p>
              </div>
              <div className="flex items-center gap-1.5">
                {a.status !== "acknowledged" && (
                  <button data-testid={`alert-ack-${a.id}`} onClick={() => ack(a)} disabled={busy}
                    className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-zinc-600 border border-zinc-300 px-2.5 py-1 rounded hover:bg-zinc-50 disabled:opacity-40">
                    <Check size={12} /> Ack</button>
                )}
                <button data-testid={`alert-dismiss-${a.id}`} onClick={() => dismiss(a)} disabled={busy}
                  className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-zinc-400 border border-zinc-200 px-2.5 py-1 rounded hover:text-red-600 hover:border-red-200 disabled:opacity-40">
                  <X size={12} /> Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
